import { Link } from 'react-router-dom';
import { articles, categories, getHotArticles, getLatestArticles, getFeaturedArticles } from '@/mocks/articles';
import ArticleCard from '@/components/base/ArticleCard';
import CategoryTabs from '@/components/base/CategoryTabs';
import SEOMeta, { generateOrganizationSchema, generateWebSiteSchema, generateFAQSchema } from '@/components/feature/SEOMeta';
import { useState } from 'react';

export default function Home() {
  const featured = getFeaturedArticles();
  const hotArticles = getHotArticles();
  const latestArticles = getLatestArticles(8);
  const [activeCategory, setActiveCategory] = useState('football');

  const categoryArticles = articles
    .filter((a) => a.category === activeCategory)
    .slice(0, 4);

  const organizationSchema = generateOrganizationSchema();
  const webSiteSchema = generateWebSiteSchema();
  const faqSchema = generateFAQSchema();

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webSiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <main>
        {/* Hero Section */}
        <section className="relative w-full">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 py-8">
              {/* Featured Article - Main */}
              {featured[0] && (
                <div className="lg:col-span-2">
                  <ArticleCard article={featured[0]} variant="featured" />
                </div>
              )}

              {/* Side featured articles */}
              <div className="flex flex-col gap-4">
                {featured.slice(1, 3).map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group block relative overflow-hidden rounded-xl h-[158px] cursor-pointer"
                  >
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/5"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <span className="inline-block px-2 py-0.5 bg-primary-500 text-white text-xs font-medium rounded-full mb-2">
                        {article.categoryName}
                      </span>
                      <h3 className="font-heading text-sm font-bold text-white line-clamp-2 group-hover:text-primary-300 transition-colors">
                        {article.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Category Quick Navigation */}
        <section className="py-6 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                赛事分类
              </h2>
              <div className="flex items-center gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-50 text-foreground-700 hover:text-primary-500 text-sm font-medium transition-all border border-background-200/70 hover:border-primary-300 cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Hot Articles */}
        <section className="py-10 md:py-14">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-accent-500 rounded-lg flex items-center justify-center">
                <i className="ri-fire-line text-white text-base"></i>
              </div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                热门资讯
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {hotArticles.map((article) => (
                <ArticleCard key={article.id} article={article} variant="default" />
              ))}
            </div>
          </div>
        </section>

        {/* Category Tabs Section */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                分类浏览
              </h2>
              <CategoryTabs
                categories={categories}
                activeSlug={activeCategory}
                onSelect={setActiveCategory}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.id} article={article} variant="default" />
              ))}
            </div>
            <div className="mt-6 text-center">
              <Link
                to={`/category/${activeCategory}`}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部{activeCategory === 'football' ? '足球' : activeCategory === 'basketball' ? '篮球' : '综合体育'}资讯
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-10 md:py-14">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
                <i className="ri-timer-line text-white text-base"></i>
              </div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                最新资讯
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {latestArticles.map((article) => (
                <ArticleCard key={article.id} article={article} variant="default" />
              ))}
            </div>
          </div>
        </section>

        {/* Stats Banner */}
        <section className="py-10 bg-foreground-950">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { value: '50万+', label: '日活用户', icon: 'ri-user-line' },
                { value: '2000+', label: '日均资讯', icon: 'ri-article-line' },
                { value: '98%', label: '用户好评率', icon: 'ri-thumb-up-line' },
                { value: '100+', label: '合作媒体', icon: 'ri-building-line' },
              ].map((stat, i) => (
                <div key={i} className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 bg-accent-500/20 rounded-full flex items-center justify-center mb-1">
                    <i className={`${stat.icon} text-accent-400 text-xl`}></i>
                  </div>
                  <span className="font-heading text-2xl md:text-3xl font-bold text-white">
                    {stat.value}
                  </span>
                  <span className="text-foreground-300 text-sm">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              常见问题
            </h2>
            <div className="space-y-3">
              {[
                { q: '乐球体育提供哪些体育项目的资讯？', a: '乐球体育提供足球、篮球以及综合体育（包括网球、田径、游泳、乒乓球、羽毛球等）的全方位赛事资讯、直播信息与数据分析。' },
                { q: '如何在乐球体育查看实时比分？', a: '您可以在乐球体育首页的实时比分板块查看正在进行的比赛比分，也可以在各个分类页面中找到对应赛事的实时数据更新。' },
                { q: '乐球体育的内容更新频率如何？', a: '乐球体育全天候24小时不间断更新体育资讯，重大赛事期间实时推送比分和战报，确保您第一时间获取最新体育动态。' },
                { q: '乐球体育是否提供赛事直播服务？', a: '乐球体育提供文字直播和实时数据更新服务，部分赛事提供视频直播链接。具体直播安排请关注网站公告和赛事页面。' },
              ].map((faq, i) => (
                <details key={i} className="group bg-background-50 rounded-xl border border-background-200/70">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-foreground-800 text-sm list-none">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="px-5 pb-4 text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}